import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system/legacy';
import { CryptoDigestAlgorithm, digest } from 'expo-crypto';
import { base64ToArrayBuffer } from './base64Helpers';

interface ChecklistMediaSourceIdentity {
  id: string;
  item_type: string;
  item_id: string;
  campo_id?: string;
  local_uri: string;
  path: string;
}

export interface RelocatedIOSChecklistMediaSource {
  uri: string;
  originalUri: string;
  size: number;
  md5: string;
  extension: string;
}

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const SOURCE = new RegExp(`^file:///[^?#%\\\\]+/Containers/Data/Application/${UUID}/Documents/` +
  `(checklist-(photos|signatures|marcacoes)/(${UUID})/(${UUID})-(${UUID})-([0-9]{13})-(${UUID})\\.(jpg|png|webp))$`);

// Após falha de compressão, a marcação preserva a captura; o envio exige a mesma identidade completa.
const CAPTURE_SOURCE = new RegExp(`^file:///[^?#%\\\\]+/Containers/Data/Application/${UUID}/Documents/` +
  `(checklist-(marcacoes)/(${UUID})/(${UUID})-(${UUID})-([0-9]{13})-(${UUID})-capture\\.(jpg|png))$`);

export const isIOSChecklistMediaPreviewUri = (value: string | null | undefined): value is string =>
  Boolean(value && (SOURCE.test(value) || CAPTURE_SOURCE.test(value)) && !value.split('/').some(part => part === '.' || part === '..'));

/** Resolve apenas a leitura do valor salvo; não depende da fila nem altera a referência ou a autoria. */
export async function resolveIOSChecklistMediaPreviewSource(
  value: string,
  scope: { companyId: string; checklistId: string; fieldId: string },
): Promise<string | null> {
  if (Platform.OS !== 'ios' || !isIOSChecklistMediaPreviewUri(value)) return value;
  const [, relative, , company, checklist, field, , , extension] = (SOURCE.exec(value) ?? CAPTURE_SOURCE.exec(value))!;
  if (company !== scope.companyId || checklist !== scope.checklistId || field !== scope.fieldId) return null;
  const source = await locateIOSChecklistMediaSource(value, relative, extension);
  if (!source) return value;
  await readRelocatedIOSChecklistMediaSource(source);
  return source.uri;
}

/** Localiza somente o mesmo arquivo relativo; não altera fila, autoria, valor ou permissão de envio. */
export async function resolveIOSChecklistMediaSource(
  pending: ChecklistMediaSourceIdentity,
  companyId: string,
): Promise<RelocatedIOSChecklistMediaSource | null> {
  if (Platform.OS !== 'ios' || pending.item_type !== 'CHECKLIST') return null;
  const match = SOURCE.exec(pending.local_uri) ?? CAPTURE_SOURCE.exec(pending.local_uri);
  if (!match || pending.local_uri.split('/').some(part => part === '.' || part === '..')) return null;
  const [, relative, , company, checklist, field, , attempt, extension] = match;
  if (company !== companyId || checklist !== pending.item_id || field !== pending.campo_id || attempt !== pending.id ||
      relative.replace(/^checklist-/, 'checklists-') !== pending.path) return null;
  return locateIOSChecklistMediaSource(pending.local_uri, relative, extension);
}

async function locateIOSChecklistMediaSource(
  originalUri: string, relative: string, extension: string,
): Promise<RelocatedIOSChecklistMediaSource | null> {
  const root = FileSystem.documentDirectory;
  if (!root || !/^file:\/\/\/[^?#%\\]+\/Documents\/$/.test(root) ||
      root.split('/').some(part => part === '.' || part === '..')) return null;
  const uri = root + relative;
  if (uri === originalUri) return null;
  // Um erro de acesso não prova ausência. Deixar a tentativa falhar preservando a fila.
  if ((await FileSystem.getInfoAsync(originalUri)).exists) return null;
  const info = await FileSystem.getInfoAsync(uri, { md5: true });
  if (!info.exists) return null;
  if (info.isDirectory || !Number.isSafeInteger(info.size) || info.size <= 0 || !/^[a-f0-9]{32}$/i.test(info.md5 ?? '')) {
    throw new Error('Arquivo localizado inválido; mídia e fila preservadas.');
  }
  return { uri, originalUri, size: info.size, md5: info.md5!, extension };
}

/** Confere bytes e estabilidade durante a leitura; o hash atual não comprova um hash histórico ausente. */
export async function readRelocatedIOSChecklistMediaSource(source: RelocatedIOSChecklistMediaSource): Promise<ArrayBuffer> {
  const base64 = await FileSystem.readAsStringAsync(source.uri, { encoding: FileSystem.EncodingType.Base64 });
  const body = base64ToArrayBuffer(base64);
  const bodyHash = Array.from(new Uint8Array(await digest(CryptoDigestAlgorithm.MD5, new Uint8Array(body))))
    .map(byte => byte.toString(16).padStart(2, '0')).join('');
  const info = await FileSystem.getInfoAsync(source.uri, { md5: true });
  if (!info.exists || info.isDirectory || info.size !== source.size || info.md5 !== source.md5 ||
      body.byteLength !== source.size || bodyHash !== source.md5.toLowerCase()) {
    throw new Error('Arquivo localizado mudou durante a leitura; mídia e fila preservadas.');
  }
  const bytes = new Uint8Array(body);
  const jpeg = bytes.length >= 4 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
  const png = bytes.length >= 8 && [137, 80, 78, 71, 13, 10, 26, 10].every((v, i) => bytes[i] === v);
  const webp = bytes.length >= 12 && [82, 73, 70, 70].every((v, i) => bytes[i] === v) &&
    [87, 69, 66, 80].every((v, i) => bytes[i + 8] === v);
  if (!(source.extension === 'jpg' ? jpeg : source.extension === 'png' ? png : webp)) {
    throw new Error('Conteúdo do arquivo localizado diverge do formato esperado; fila preservada.');
  }
  return body;
}
